// Customer-facing page for a secure link. No login; shows only what the link allows.
import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { Alert, Button, TextInput } from "../ui";
import { formatGBP } from "../../shared/money";
import { formatDateUK } from "../../shared/dates";
import { qtyLabel } from "../format";

export default function PublicLink({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const load = () => api.get(`/public/links/${token}`).then(setData).catch((e) => setError(e instanceof ApiError ? e.message : "Could not load this page."));
  useEffect(() => { document.title = "Wali Auto Fix"; load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const respond = async (decision: "accept" | "decline") => {
    setBusy(decision); setError(null);
    try {
      await api.post(`/public/links/${token}/respond`, { decision, revisionNo: data.quote.revisionNo, name });
      setDone(decision === "accept" ? "Thank you — your approval has been sent to the garage. We'll be in touch to arrange the work." : "Thanks for letting us know. The garage has been told you don't wish to go ahead.");
    } catch (e) { setError(e instanceof Error ? e.message : "Something went wrong."); await load(); } finally { setBusy(null); }
  };
  return (
    <div style={{ minHeight: "100vh", background: "var(--grey-50)", padding: 16 }}>
      <div className="card" style={{ maxWidth: 680, margin: "0 auto", padding: 20 }}>
        <img src="/logo.png" alt="Wali Autofix Ltd" style={{ width: "100%", maxWidth: 360, height: "auto" }} />
        {!data && !error && <p className="muted">Loading…</p>}
        {error && <div style={{ marginTop: 12 }}><Alert kind="error">{error}</Alert></div>}
        {data && (
          <div className="stack" style={{ marginTop: 12 }}>
            <h1>Hi {data.greeting}</h1>
            {data.vehicle && <p><span className="reg-plate">{data.vehicle.registration}</span> {data.vehicle.description}</p>}
            {data.kind === "job" && (
              <>
                <div className="alert info"><strong>Status: {data.job.statusLabel}</strong>Job {data.job.number}{data.job.promisedDate ? ` · expected by ${formatDateUK(data.job.promisedDate)}` : ""}</div>
                {data.job.status === "ready" && <p>Your car is ready to collect. We're open {data.business.hours}.</p>}
              </>
            )}
            {data.kind === "quote" && (
              <>
                <h2>Quote {data.quote.number}{data.quote.revisionNo > 1 ? ` (revision ${data.quote.revisionNo})` : ""}</h2>
                <div className="table-wrap"><table className="table">
                  <thead><tr><th>Work</th><th className="num">Qty</th><th className="num">Price</th></tr></thead>
                  <tbody>{data.quote.lines.map((l: any, i: number) => <tr key={i}><td>{l.description}</td><td className="num">{qtyLabel(l.qty_milli)}</td><td className="num">{formatGBP(l.line_total_pence)}</td></tr>)}</tbody>
                  <tfoot>
                    {data.quote.totals.vatRegistered && <><tr><td colSpan={2} className="right">Net</td><td className="num">{formatGBP(data.quote.totals.net)}</td></tr><tr><td colSpan={2} className="right">VAT</td><td className="num">{formatGBP(data.quote.totals.vat)}</td></tr></>}
                    <tr><td colSpan={2} className="right">Total</td><td className="num">{formatGBP(data.quote.totals.total)}</td></tr>
                  </tfoot>
                </table></div>
                {data.quote.notes && <p style={{ whiteSpace: "pre-wrap" }}>{data.quote.notes}</p>}
                {done ? <Alert kind="success">{done}</Alert> : ["draft", "sent"].includes(data.quote.status) ? (
                  <div className="stack">
                    <TextInput label="Your name (to confirm)" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                    <div className="row">
                      <Button variant="primary" disabled={name.trim().length < 2} loading={busy === "accept"} onClick={() => respond("accept")}>Approve this quote</Button>
                      <Button variant="danger" disabled={name.trim().length < 2} loading={busy === "decline"} onClick={() => respond("decline")}>No thanks</Button>
                    </div>
                    <p className="small muted">Approving lets the garage go ahead with the work listed above at the price shown.</p>
                  </div>
                ) : <Alert kind="info">This quote is {data.quote.status}. Please call us if you have any questions.</Alert>}
              </>
            )}
            <p className="small muted">Questions? Call {data.business.name} on <a href={`tel:${data.business.phone.replace(/\s/g, "")}`}>{data.business.phone}</a> · {data.business.address}</p>
          </div>
        )}
      </div>
    </div>
  );
}
